import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import MapView from "@/components/app/MapView";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, Clock, Calendar, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";

const SPECIALTIES = [
  "all", "Médecine générale", "Cardiologie", "Pédiatrie", "Dermatologie", "Gynécologie",
  "Psychiatrie", "Ophtalmologie", "ORL", "Dentaire",
];

export default function PatientSearch() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [specialty, setSpecialty] = useState(params.get("specialty") || "all");
  const [onlyAvailable, setOnlyAvailable] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [slots, setSlots] = useState([]);
  const [mode, setMode] = useState("video");

  const runSearch = async () => {
    setLoading(true);
    try {
      const params = { only_available: onlyAvailable };
      if (q) params.q = q;
      if (specialty && specialty !== "all") params.specialty = specialty;
      if (userPos) { params.lat = userPos[0]; params.lng = userPos[1]; params.radius_km = 500; }
      const { data } = await api.get("/doctors/search", { params });
      setDoctors(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => setUserPos([p.coords.latitude, p.coords.longitude]),
        () => setUserPos([48.8566, 2.3522]) // Paris default
      );
    }
  }, []);

  useEffect(() => { runSearch(); /* eslint-disable-next-line */ }, [onlyAvailable]);

  const openDoctor = async (d) => {
    setSelected(d);
    try {
      const { data } = await api.get("/slots", { params: { doctor_id: d.user_id } });
      setSlots(data.filter((s) => s.status === "available"));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const book = async (slot) => {
    try {
      const { data } = await api.post("/appointments", { slot_id: slot.slot_id, reason: "", mode });
      if (data.requires_card_setup) {
        toast.success("Rendez-vous réservé — enregistrez votre carte pour le confirmer.");
        const res = await api.post(`/appointments/${data.appointment_id}/card-setup`, { origin_url: window.location.origin });
        window.location.href = res.data.url;
        return;
      }
      toast.success("Rendez-vous confirmé ! Email + SMS envoyés (simulés).");
      setSelected(null);
      runSearch();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-2">Trouver un praticien</div>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Recherche géolocalisée</h1>
        <p className="text-slate-500 mt-2">Les praticiens disponibles dans les 14 prochains jours sont mis en avant.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 grid lg:grid-cols-12 gap-4 mb-6 items-end">
        <div className="lg:col-span-5">
          <Label className="text-xs text-slate-500">Recherche libre</Label>
          <div className="mt-1.5 relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Nom, spécialité, ville…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" data-testid="search-query-input" />
          </div>
        </div>
        <div className="lg:col-span-3">
          <Label className="text-xs text-slate-500">Spécialité</Label>
          <Select value={specialty} onValueChange={setSpecialty}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="search-specialty-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Toutes spécialités" : s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2 flex items-center gap-2">
          <Switch checked={onlyAvailable} onCheckedChange={setOnlyAvailable} id="onlyavail" data-testid="search-only-available-switch" />
          <Label htmlFor="onlyavail" className="text-sm">Disponibles uniquement</Label>
        </div>
        <div className="lg:col-span-2">
          <Button onClick={runSearch} disabled={loading} className="w-full h-11 btn-primary" data-testid="search-submit-btn">
            {loading ? "Recherche…" : "Rechercher"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-4">
          {doctors.length === 0 && !loading && (
            <div className="text-center text-slate-500 py-12 border border-dashed border-slate-300 rounded-xl">
              Aucun praticien trouvé. Essayez d'élargir vos critères.
            </div>
          )}
          {doctors.map((d) => (
            <div key={d.user_id} className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#0FACD7] hover:shadow-md transition" data-testid={`doctor-card-${d.user_id}`}>
              <div className="flex items-start gap-5">
                <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-50 to-orange-50 text-[#0FACD7] flex items-center justify-center font-display text-2xl font-extrabold flex-shrink-0">
                  {d.name.replace(/^Dr\s+/, "").charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-display text-lg font-extrabold text-slate-900 flex items-center gap-1.5">
                        {d.name}
                        {d.rpps_verified && <span title="Praticien vérifié Pro Santé Connect" className="text-[#0FACD7]"><svg className="h-4 w-4 inline" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg></span>}
                      </div>
                      <div className="text-sm text-slate-500 font-medium">{d.specialty}</div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-sm">
                        <span className="star">★★★★★</span>
                        <span className="text-slate-700 font-semibold">4,9</span>
                        <span className="text-slate-400">· {120 + (d.name.length * 7) % 200} avis</span>
                      </div>
                    </div>
                    {d.available_slots_14d > 0 && (
                      <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 whitespace-nowrap font-semibold">
                        Disponible
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {d.city || "—"} {d.distance_km != null && <span className="text-[#0FACD7] font-bold">· {d.distance_km} km</span>}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {d.consultation_duration} min</span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold text-[11px]">Sécu + Mutuelle</span>
                  </div>
                  {d.next_available && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 font-semibold">
                      <Calendar className="h-4 w-4" /> Prochain : {new Date(d.next_available).toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  )}
                  {d.bio && <p className="mt-3 text-sm text-slate-600 line-clamp-2">{d.bio}</p>}
                  <div className="mt-5">
                    <Button onClick={() => openDoctor(d)} className="btn-coral font-bold" data-testid={`book-${d.user_id}-btn`}>
                      Réserver un créneau
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 h-[600px] lg:sticky lg:top-6">
          <MapView center={userPos || [48.8566, 2.3522]} doctors={doctors} userPos={userPos} onSelect={openDoctor} />
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{selected?.name}</DialogTitle>
            <DialogDescription>
              {selected?.specialty} · {selected?.address}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex gap-2 mb-4" data-testid="booking-mode-toggle">
              {[["video", "Téléconsultation vidéo"], ["cabinet", "Au cabinet"]].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setMode(m)} data-testid={`booking-mode-${m}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${mode === m ? "bg-[#2A4365] text-white border-[#2A4365]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                  {label}
                </button>
              ))}
            </div>
            {mode === "video" && selected?.card_payments_enabled && (
              <div className="mb-4 text-xs bg-blue-50 border border-blue-100 text-blue-800 rounded-lg p-3" data-testid="booking-card-notice">
                <b>Paiement sécurisé à l'avance</b> : vous enregistrez votre carte à la réservation (aucun débit immédiat). Les <b>{selected.consultation_fee} €</b> sont prélevés
                automatiquement à la fin de la téléconsultation, directement au profit du praticien. Annulation gratuite jusqu'à 24 h avant.
              </div>
            )}
            <div className="text-sm font-medium mb-3">Créneaux disponibles</div>
            {slots.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun créneau pour le moment.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-auto">
                {slots.map((s) => {
                  const d = new Date(s.start);
                  return (
                    <button
                      key={s.slot_id}
                      onClick={() => book(s)}
                      data-testid={`slot-${s.slot_id}`}
                      className="agenda-slot text-left p-3 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition"
                    >
                      <div className="text-xs text-emerald-700 uppercase tracking-wider">{d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}</div>
                      <div className="font-display font-semibold text-slate-900 mt-1">{d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                      <div className="text-xs text-slate-500">{s.duration_min} min</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
